import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useProductStore } from '../../context/ProductContext';
import { useAuthStore } from '../../context/AuthContext';
import { marpolRegulationToFAIcon } from '../../utils/iconMapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faPlus, faBox, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useEditMode } from '../../context/EditModeContext';
import { getProductHistory } from '../../utils/productHistory';
import { ProductListItem } from '../../types/ProductTypes';
import useProductQuery from '../../api/useProductQuery';
import { sortByRelevance, highlightMatch } from '../../utils/searchUtils';
import { formatCategoryName } from '../../utils/categoryNameFormatter';
import { useChartererStore, enrichWithChartererNames } from '../../context/ChartererContext';
import {
    ProductsListContainer,
    ProductsHeader,
    LoadingContainer,
    LoadingSpinner,
    SearchSection,
    SearchInputWrapper,
    SearchInput,
    EmptyState,
    ProductsListView,
    ListHeader,
    ListRowsContainer,
    HeaderCell,
    ListRow,
    ListCell,
    LoadMoreContainer,
    LoadMoreButton,
    LoadMoreInfo,
    SearchLoadingIndicator,
    FilterToggleButton,
    FilterPanel,
    FilterGroup,
    FilterGroupTitle,
    FilterCheckboxGroup,
    ActiveFilterContainer,
    ActiveFilterLabel,
    MobileFilterToggle,
    MobileFilterPanel,
    InlineFiltersRow,
    NewCategoryButton,
} from './ProductsList.styles';
import { ToggleSwitch } from '../shared/ToggleSwitch';
import { StatusBadge } from '../shared/StatusBadge';

const INITIAL_LOAD = 100;
const LOAD_MORE_COUNT = 100;
const SEARCH_MAX_PAGE_SIZE = 5000;
const SEARCH_DEBOUNCE_MS = 300;

interface ProductsListProps {
    showPreviouslyVisited?: boolean;
    hideSearch?: boolean;
    hideHeader?: boolean;
}

const ProductsList: React.FC<ProductsListProps> = ({ showPreviouslyVisited = false, hideSearch = false, hideHeader = false }) => {
    const { productListItems, setProductListItems, pagination, setPagination, setIsProductListRetrieved } = useProductStore();
    const { queryProducts, isLoading } = useProductQuery();
    const { canEdit, canEditChemGuard } = useAuthStore();
    const hasEditorRole = canEdit || canEditChemGuard;
    const { isInEditMode } = useEditMode();
    const { charterers } = useChartererStore();
    const navigate = useNavigate();
    const { setQueryParamProductId } = queryParamsUtils();
    const [searchTerm, setSearchTerm] = useState<string>(() => {
        return sessionStorage.getItem('cbp.lastSearch') || '';
    });
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(() => {
        return sessionStorage.getItem('cbp.lastSearch') || '';
    });
    const [scrollHeight, setScrollHeight] = useState<number>(0);
    const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
    const [loadedCount, setLoadedCount] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const isAppendingRef = useRef<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
    const [searchOptions, setSearchOptions] = useState({
        searchProductCategoryNames: true,
        searchTradeNames: true,
        searchSynonyms: true,
        searchChartererNames: true,
        searchCasNumber: true,
    });
    const [filterCriteria, setFilterCriteria] = useState({
        marpolRegulationIsAnnexI: false,
        marpolRegulationIsAnnexIIChapter17: false,
        marpolRegulationIsAnnexIIChapter18: false,
        marpolRegulationIsGas: false,
    });
    const [showInactive, setShowInactive] = useState<boolean>(hasEditorRole);
    const [showActive, setShowActive] = useState<boolean>(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            // Persist search term so product detail page split view can pick it up
            sessionStorage.setItem('cbp.lastSearch', searchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    const isSearchActive = !!debouncedSearchTerm?.trim();

    // Reset and load initial batch (called on search/filter changes)
    const resetAndFetch = useCallback(async () => {
        isAppendingRef.current = false;
        try {
            // Build MARPOL regulation codes from filter criteria
            const marpolRegulationCodes: number[] = [];
            if (filterCriteria.marpolRegulationIsAnnexI) marpolRegulationCodes.push(1);
            if (filterCriteria.marpolRegulationIsAnnexIIChapter17) marpolRegulationCodes.push(2);
            if (filterCriteria.marpolRegulationIsAnnexIIChapter18) marpolRegulationCodes.push(3);
            if (filterCriteria.marpolRegulationIsGas) marpolRegulationCodes.push(4);

            // When searching: fetch ALL matches so client-side relevance sort sees the full set.
            // When browsing (no search): load initial batch.
            const isSearching = !!debouncedSearchTerm?.trim();

            const result = await queryProducts({
                search: debouncedSearchTerm || undefined,
                page: 1,
                pageSize: isSearching ? SEARCH_MAX_PAGE_SIZE : INITIAL_LOAD,
                includeInactive: showInactive ? undefined : false,
                searchTradeNames: searchOptions.searchTradeNames ? undefined : false,
                searchCategoryNames: searchOptions.searchProductCategoryNames ? undefined : false,
                searchSynonyms: searchOptions.searchSynonyms ? undefined : false,
                searchCasNumber: searchOptions.searchCasNumber ? undefined : false,
                marpolRegulationCodes: marpolRegulationCodes.length > 0 ? marpolRegulationCodes : undefined,
            });

            let items = result.items;

            // Client-side charterer name search (OR semantics with text search)
            if (searchOptions.searchChartererNames && debouncedSearchTerm?.trim() && charterers.length > 0) {
                const term = debouncedSearchTerm.trim().toLowerCase();
                const matchingChartererIds = charterers
                    .filter((c) => c.name.toLowerCase().includes(term))
                    .map((c) => c.id);

                if (matchingChartererIds.length > 0) {
                    const chartererResult = await queryProducts({
                        chartererIds: matchingChartererIds,
                        page: 1,
                        pageSize: SEARCH_MAX_PAGE_SIZE,
                        includeInactive: showInactive ? undefined : false,
                        marpolRegulationCodes: marpolRegulationCodes.length > 0 ? marpolRegulationCodes : undefined,
                    });
                    // Merge with OR semantics: deduplicate by product ID
                    const existingIds = new Set(items.map((i) => i.id));
                    const additional = chartererResult.items.filter((i) => !existingIds.has(i.id));
                    items = [...items, ...additional];
                }
            }

            const finalCount = isSearching ? items.length : result.totalCount;
            setProductListItems(items);
            setLoadedCount(items.length);
            setTotalCount(finalCount);
            setPagination({
                totalCount: finalCount,
                pageNumber: 1,
                pageSize: isSearching ? SEARCH_MAX_PAGE_SIZE : INITIAL_LOAD,
                totalPages: Math.ceil(finalCount / (isSearching ? SEARCH_MAX_PAGE_SIZE : INITIAL_LOAD)),
            });
            setIsProductListRetrieved(true);
            setHasInitialLoad(true);
        } catch (error) {
            console.error('[ProductsList] Error fetching products:', error);
            setProductListItems([]);
            setLoadedCount(0);
            setTotalCount(0);
            setIsProductListRetrieved(true);
            setHasInitialLoad(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, queryProducts, canEdit, showInactive, showActive, searchOptions, filterCriteria, charterers]);

    // Load more items (append to existing list)
    const handleLoadMore = useCallback(async () => {
        if (isLoading) return;
        isAppendingRef.current = true;
        try {
            const marpolRegulationCodes: number[] = [];
            if (filterCriteria.marpolRegulationIsAnnexI) marpolRegulationCodes.push(1);
            if (filterCriteria.marpolRegulationIsAnnexIIChapter17) marpolRegulationCodes.push(2);
            if (filterCriteria.marpolRegulationIsAnnexIIChapter18) marpolRegulationCodes.push(3);
            if (filterCriteria.marpolRegulationIsGas) marpolRegulationCodes.push(4);

            // Calculate the next page to fetch (loadedCount / pageSize gives pages already loaded)
            const nextPage = Math.floor(loadedCount / LOAD_MORE_COUNT) + 1;

            const result = await queryProducts({
                search: debouncedSearchTerm || undefined,
                page: nextPage,
                pageSize: LOAD_MORE_COUNT,
                includeInactive: showInactive ? undefined : false,
                searchTradeNames: searchOptions.searchTradeNames ? undefined : false,
                searchCategoryNames: searchOptions.searchProductCategoryNames ? undefined : false,
                searchSynonyms: searchOptions.searchSynonyms ? undefined : false,
                searchChartererNames: searchOptions.searchChartererNames ? undefined : false,
                searchCasNumber: searchOptions.searchCasNumber ? undefined : false,
                marpolRegulationCodes: marpolRegulationCodes.length > 0 ? marpolRegulationCodes : undefined,
            });

            // Append new items to the existing list
            setProductListItems([...productListItems, ...result.items]);
            setLoadedCount((prev) => prev + result.items.length);
            setTotalCount(result.totalCount);
        } catch (error) {
            console.error('[ProductsList] Error loading more products:', error);
        } finally {
            isAppendingRef.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, loadedCount, productListItems, debouncedSearchTerm, queryProducts, showInactive, searchOptions, filterCriteria]);

    // Initial load and search/filter change handler
    useEffect(() => {
        // Skip if showing previously visited and no search
        if (showPreviouslyVisited && !debouncedSearchTerm) {
            setIsProductListRetrieved(true);
            setHasInitialLoad(true);
            return;
        }

        resetAndFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, showPreviouslyVisited, searchOptions, filterCriteria, showInactive, showActive, charterers]);

    // Derive enriched products reactively — recalculates whenever either input changes,
    // eliminating the race condition between charterer and product list loading.
    const enrichedProductListItems = useMemo(
        () => enrichWithChartererNames(productListItems, charterers),
        [productListItems, charterers]
    );

    // Calculate scroll container height
    useEffect(() => {
        const calculateHeight = () => {
            if (!containerRef.current || !headerRef.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const headerRect = headerRef.current.getBoundingClientRect();
            const remainingHeight = window.innerHeight - containerRect.top - headerRect.height - 32; // 32px for padding

            setScrollHeight(Math.max(remainingHeight, 200)); // Minimum 200px height
        };

        calculateHeight();

        // Recalculate on window resize
        const resizeObserver = new ResizeObserver(() => {
            calculateHeight();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', calculateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', calculateHeight);
        };
    }, []);

    const getDisplayedProducts = (): ProductListItem[] => {
        // If showPreviouslyVisited is true and no search is active, show history
        if (showPreviouslyVisited && !searchTerm.trim()) {
            return getProductHistory();
        }

        return enrichedProductListItems;
    };

    const rawDisplayedProducts = getDisplayedProducts();

    // Apply client-side relevance sorting when a search term is active
    // and client-side active/inactive filtering.
    const displayedProducts = useMemo(() => {
        let items = rawDisplayedProducts;

        // Client-side active/inactive filter (API has no onlyInactive param)
        if (!showActive && showInactive) {
            items = items.filter((p) => !p.isActive);
        } else if (showActive && !showInactive) {
            // Already handled server-side via includeInactive: false
        } else if (!showActive && !showInactive) {
            items = [];
        }

        // Non-writer users only see trade name products, not default products (product categories)
        if (!hasEditorRole) {
            items = items.filter((p) => !!p.tradeName);
        }

        if (isSearchActive) {
            // Full relevance sort on the complete result set
            items = sortByRelevance(items, debouncedSearchTerm);
        }

        return items;
    }, [rawDisplayedProducts, debouncedSearchTerm, isSearchActive, showActive, showInactive, hasEditorRole]);

    const isSearching = !!debouncedSearchTerm?.trim();
    const hasMore = !isSearching && loadedCount < totalCount;

    // Determine header title based on context
    const headerTitle = showPreviouslyVisited && !searchTerm.trim() ? 'Previously Visited' : 'Products';
    const displayCount = showPreviouslyVisited && !searchTerm.trim()
        ? displayedProducts.length
        : isSearching ? displayedProducts.length : totalCount;

    const handleProductClick = (productId: string | number) => {
        // Navigate to product page
        navigate('/product');
        // Set the product ID as a query parameter
        setQueryParamProductId(productId.toString());
    };

    const toggleAllSearchOptions = () => {
        const allSelected = Object.values(searchOptions).every((v) => v);
        setSearchOptions({
            searchProductCategoryNames: !allSelected,
            searchTradeNames: !allSelected,
            searchSynonyms: !allSelected,
            searchChartererNames: !allSelected,
            searchCasNumber: !allSelected,
        });
    };

    const toggleAllFilterCriteria = () => {
        const allSelected = Object.values(filterCriteria).every((v) => v);
        setFilterCriteria({
            marpolRegulationIsAnnexI: !allSelected,
            marpolRegulationIsAnnexIIChapter17: !allSelected,
            marpolRegulationIsAnnexIIChapter18: !allSelected,
            marpolRegulationIsGas: !allSelected,
        });
    };

    const handleSearchOptionChange = (option: keyof typeof searchOptions) => {
        setSearchOptions((prev) => ({ ...prev, [option]: !prev[option] }));
    };

    const handleFilterCriteriaChange = (criteria: keyof typeof filterCriteria) => {
        setFilterCriteria((prev) => ({ ...prev, [criteria]: !prev[criteria] }));
    };

    const hasActiveFilters = !Object.values(searchOptions).every((v) => v) || Object.values(filterCriteria).some((v) => v);

    return (
        <ProductsListContainer ref={containerRef}>
            <div ref={headerRef}>
            </div>

            {/* Search Section */}
            {!hideSearch && (
                <>
                    {canEdit && isInEditMode && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                            <NewCategoryButton
                                onClick={() => navigate('/product?newCategory=true')}
                                title="Create a new product category"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                New Product Category
                            </NewCategoryButton>
                        </div>
                    )}
                    <SearchSection>
                        <SearchInputWrapper>
                            <SearchInput
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {isLoading && <SearchLoadingIndicator />}
                        </SearchInputWrapper>
                        {hasEditorRole && (
                            <InlineFiltersRow>
                                <ActiveFilterContainer>
                                    <ActiveFilterLabel>Active</ActiveFilterLabel>
                                    <ToggleSwitch
                                        checked={showActive}
                                        onChange={(checked) => setShowActive(checked)}
                                        size="tiny"
                                        hideStatusText
                                    />
                                </ActiveFilterContainer>
                                <ActiveFilterContainer>
                                    <ActiveFilterLabel>Inactive</ActiveFilterLabel>
                                    <ToggleSwitch
                                        checked={showInactive}
                                        onChange={(checked) => setShowInactive(checked)}
                                        size="tiny"
                                        hideStatusText
                                    />
                                </ActiveFilterContainer>
                            </InlineFiltersRow>
                        )}
                        <FilterToggleButton
                            $active={showFilters || hasActiveFilters}
                            onClick={() => setShowFilters(!showFilters)}
                            title="Filter options"
                        >
                            <FontAwesomeIcon icon={faFilter} />
                            Filters{hasActiveFilters ? ' ●' : ''}
                        </FilterToggleButton>
                        {/* Mobile-only filter toggle button */}
                        <MobileFilterToggle
                            $active={showMobileFilters}
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            title="Toggle filters"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            <svg className="chevron" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7 10l5 5 5-5z" />
                            </svg>
                        </MobileFilterToggle>
                    </SearchSection>
                    {/* Mobile collapsible filter panel */}
                    {showMobileFilters && (
                        <MobileFilterPanel>
                            {hasEditorRole && (
                                <div className="mobile-toggles-row">
                                    <ActiveFilterContainer>
                                        <ActiveFilterLabel>Active</ActiveFilterLabel>
                                        <ToggleSwitch
                                            checked={showActive}
                                            onChange={(checked) => setShowActive(checked)}
                                            size="tiny"
                                            hideStatusText
                                        />
                                    </ActiveFilterContainer>
                                    <ActiveFilterContainer>
                                        <ActiveFilterLabel>Inactive</ActiveFilterLabel>
                                        <ToggleSwitch
                                            checked={showInactive}
                                            onChange={(checked) => setShowInactive(checked)}
                                            size="tiny"
                                            hideStatusText
                                        />
                                    </ActiveFilterContainer>
                                </div>
                            )}
                            <div className="mobile-filter-groups-row">
                            <FilterGroup>
                                <FilterGroupTitle>Search In:</FilterGroupTitle>
                                <FilterCheckboxGroup>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={Object.values(searchOptions).every((v) => v)}
                                            onChange={toggleAllSearchOptions}
                                        />
                                        (Select All)
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchProductCategoryNames}
                                            onChange={() => handleSearchOptionChange('searchProductCategoryNames')}
                                        />
                                        Product
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchTradeNames}
                                            onChange={() => handleSearchOptionChange('searchTradeNames')}
                                        />
                                        Trade Name
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchSynonyms}
                                            onChange={() => handleSearchOptionChange('searchSynonyms')}
                                        />
                                        Synonyms
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchChartererNames}
                                            onChange={() => handleSearchOptionChange('searchChartererNames')}
                                        />
                                        Charterer
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchCasNumber}
                                            onChange={() => handleSearchOptionChange('searchCasNumber')}
                                        />
                                        CAS number
                                    </label>
                                </FilterCheckboxGroup>
                            </FilterGroup>
                            <FilterGroup>
                                <FilterGroupTitle>Filter:</FilterGroupTitle>
                                <FilterCheckboxGroup>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={Object.values(filterCriteria).every((v) => v)}
                                            onChange={toggleAllFilterCriteria}
                                        />
                                        (Select All)
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsAnnexI}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexI')}
                                        />
                                        Annex I
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsAnnexIIChapter17}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexIIChapter17')}
                                        />
                                        Annex II Chapter 17
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsAnnexIIChapter18}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexIIChapter18')}
                                        />
                                        Annex II Chapter 18
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsGas}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsGas')}
                                        />
                                        Gas
                                    </label>
                                </FilterCheckboxGroup>
                            </FilterGroup>
                            </div>
                        </MobileFilterPanel>
                    )}
                </>
            )}

            {/* Filter Options Panel */}
            {!hideSearch && showFilters && (
                <FilterPanel>
                    <FilterGroup>
                        <FilterGroupTitle>Search In:</FilterGroupTitle>
                        <FilterCheckboxGroup>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={Object.values(searchOptions).every((v) => v)}
                                    onChange={toggleAllSearchOptions}
                                />
                                (Select All)
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={searchOptions.searchProductCategoryNames}
                                    onChange={() => handleSearchOptionChange('searchProductCategoryNames')}
                                />
                                Product
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={searchOptions.searchTradeNames}
                                    onChange={() => handleSearchOptionChange('searchTradeNames')}
                                />
                                Trade Name
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={searchOptions.searchSynonyms}
                                    onChange={() => handleSearchOptionChange('searchSynonyms')}
                                />
                                Synonyms
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={searchOptions.searchChartererNames}
                                    onChange={() => handleSearchOptionChange('searchChartererNames')}
                                />
                                Charterer
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={searchOptions.searchCasNumber}
                                    onChange={() => handleSearchOptionChange('searchCasNumber')}
                                />
                                CAS number
                            </label>
                        </FilterCheckboxGroup>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterGroupTitle>Filter:</FilterGroupTitle>
                        <FilterCheckboxGroup>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={Object.values(filterCriteria).every((v) => v)}
                                    onChange={toggleAllFilterCriteria}
                                />
                                (Select All)
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={filterCriteria.marpolRegulationIsAnnexI}
                                    onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexI')}
                                />
                                Annex I
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={filterCriteria.marpolRegulationIsAnnexIIChapter17}
                                    onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexIIChapter17')}
                                />
                                Annex II Chapter 17
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={filterCriteria.marpolRegulationIsAnnexIIChapter18}
                                    onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexIIChapter18')}
                                />
                                Annex II Chapter 18
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={filterCriteria.marpolRegulationIsGas}
                                    onChange={() => handleFilterCriteriaChange('marpolRegulationIsGas')}
                                />
                                Gas
                            </label>
                        </FilterCheckboxGroup>
                    </FilterGroup>
                </FilterPanel>
            )}

            {!hasInitialLoad ? (
                <LoadingContainer>
                    <LoadingSpinner />
                    <p>Loading products...</p>
                </LoadingContainer>
            ) : displayedProducts?.length === 0 ? (
                <EmptyState>
                    <p>No products found.</p>
                </EmptyState>
            ) : (
                <ProductsListView>
                    <ListHeader>
                        <HeaderCell className="icon-header"></HeaderCell>
                        <HeaderCell>Trade Name</HeaderCell>
                        <HeaderCell>Product Name (IBC)</HeaderCell>
                        <HeaderCell>Charterer</HeaderCell>
                        <HeaderCell>CAS Number</HeaderCell>
                        <HeaderCell>Type</HeaderCell>
                        <HeaderCell>Status</HeaderCell>
                    </ListHeader>
                    <ListRowsContainer style={{ height: scrollHeight - (hasMore ? 50 : 0) }}>
                        {displayedProducts.map((product: ProductListItem, index: number) => {
                            const marpolRegNum = parseInt(product.marpolRegulation || '0', 10);
                            const formattedCategory = formatCategoryName(product.categoryName, product.tradeName, product.contains);
                            const tooltipText = `Product Category: ${formattedCategory || ''}${product.synonym ? `\nSynonyms: ${product.synonym}` : ''}`;
                            return (
                                <ListRow key={product.id || index} $clickable $inactive={!product.isActive} onClick={() => handleProductClick(product.id)} title={tooltipText}>
                                    <ListCell className="icon-cell">
                                        <FontAwesomeIcon
                                            icon={marpolRegulationToFAIcon(marpolRegNum).icon as any}
                                            style={{ color: marpolRegulationToFAIcon(marpolRegNum).color, fontSize: '16px' }}
                                        />
                                    </ListCell>
                                    <ListCell className="product-name-cell">
                                        <span className="product-name">{highlightMatch(product.tradeName || formattedCategory || '', debouncedSearchTerm)}</span>
                                    </ListCell>
                                    <ListCell className="category-cell">{highlightMatch(formattedCategory || 'Unknown', debouncedSearchTerm)}</ListCell>
                                    <ListCell className="charterer-cell">{highlightMatch(product.chartererName || '-', debouncedSearchTerm)}</ListCell>
                                    <ListCell className="cas-cell">{highlightMatch(product.casNumber || '-', debouncedSearchTerm)}</ListCell>
                                    <ListCell className="type-cell">
                                        <FontAwesomeIcon
                                            icon={!product.tradeName ? faLayerGroup : faBox}
                                            title={!product.tradeName ? 'Default Product' : 'Product'}
                                            style={{ color: !product.tradeName ? '#6c757d' : '#0d6efd', fontSize: '14px' }}
                                        />
                                    </ListCell>
                                    <ListCell className="status-cell">
                                        <StatusBadge variant={product.isActive ? 'active' : 'inactive'} size="list">
                                            {product.isActive ? 'Active' : 'Inactive'}
                                        </StatusBadge>
                                    </ListCell>
                                </ListRow>
                            );
                        })}
                    </ListRowsContainer>
                    {hasMore && (
                        <LoadMoreContainer>
                            <LoadMoreButton
                                onClick={handleLoadMore}
                                disabled={isLoading}
                                $disabled={isLoading}
                            >
                                {isLoading ? 'Loading...' : `Load ${LOAD_MORE_COUNT} more`}
                            </LoadMoreButton>
                            <LoadMoreInfo>
                                Showing {loadedCount} of {totalCount}
                            </LoadMoreInfo>
                        </LoadMoreContainer>
                    )}
                </ProductsListView>
            )}
        </ProductsListContainer>
    );
};

export default ProductsList;
